'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Lock } from 'assets/icons/Lock';
import CustomLink from 'components/shared/CustomLink';
import type { ConsentCategorySelection } from 'lib/consent/policy';
import styles from 'components/shared/CookieConsentBanner.module.css';
import type { GrantedCategories } from './ConsentRuntime';

export const COOKIE_POLICY_URL =
  'https://ultrahumanapp.notion.site/Cookie-Policy-ebcff6b1c95744fb9471ee874fc544e7';

type PrivacyChoicesDialogProps = {
  open: boolean;
  gpc: boolean;
  granted: GrantedCategories;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
  onSave: (categories: ConsentCategorySelection) => void;
};

// The ::backdrop dispatches to the <dialog>, so check the pointer is outside its box.
function isBackdropClick(
  dialog: HTMLDialogElement,
  event: MouseEvent,
): boolean {
  if (event.target !== dialog) return false;
  const box = dialog.getBoundingClientRect();
  return (
    event.clientX < box.left ||
    event.clientX > box.right ||
    event.clientY < box.top ||
    event.clientY > box.bottom
  );
}

/**
 * Keep mounted: `open` drives showModal()/close(), and the native close
 * returns focus to the control that opened it.
 */
export function PrivacyChoicesDialog({
  open,
  gpc,
  granted,
  onClose,
  onAccept,
  onDecline,
  onSave,
}: PrivacyChoicesDialogProps) {
  const t = useTranslations('common');
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [stats, setStats] = useState(granted.stats);
  const [analytics, setAnalytics] = useState(granted.analytics);
  const [ads, setAds] = useState(granted.ads);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      setStats(granted.stats);
      setAnalytics(granted.analytics);
      setAds(gpc ? false : granted.ads);
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open, granted.ads, granted.analytics, granted.stats, gpc]);

  // Close on a backdrop click. Both ends must be outside, so a text selection
  // dragged out of the panel does not close it.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    let pressedOutside = false;
    const down = (event: MouseEvent) => {
      pressedOutside = isBackdropClick(dialog, event);
    };
    const click = (event: MouseEvent) => {
      if (pressedOutside && isBackdropClick(dialog, event)) onClose();
      pressedOutside = false;
    };
    dialog.addEventListener('mousedown', down);
    dialog.addEventListener('click', click);
    return () => {
      dialog.removeEventListener('mousedown', down);
      dialog.removeEventListener('click', click);
    };
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      // Escape closes natively; this keeps `open` in sync.
      onClose={() => {
        if (open) onClose();
      }}
    >
      <h2 id={titleId} className={styles.dialogTitle}>
        {t('cookieBanner.dialogTitle')}
      </h2>
      <p id={descriptionId} className={styles.dialogDescription}>
        {t('cookieBanner.dialogDescription')}
      </p>
      {gpc ? (
        <div className={styles.dialogMeta}>
          <p className={styles.gpcStatus}>{t('cookieBanner.gpcHonored')}</p>
        </div>
      ) : null}
      <ul className={styles.switchList}>
        <SwitchRow
          label={t('cookieBanner.essentialLabel')}
          description={t('cookieBanner.essentialDescription')}
          checked
          disabled
          locked
        />
        <SwitchRow
          label={t('cookieBanner.statsLabel')}
          description={t('cookieBanner.statsDescription')}
          checked={stats}
          onChange={setStats}
        />
        <SwitchRow
          label={t('cookieBanner.analyticsLabel')}
          description={t('cookieBanner.analyticsDescription')}
          checked={analytics}
          onChange={setAnalytics}
        />
        <SwitchRow
          label={t('cookieBanner.adsLabel')}
          description={t('cookieBanner.adsDescription')}
          note={gpc ? t('cookieBanner.gpcNote') : undefined}
          checked={gpc ? false : ads}
          disabled={gpc}
          onChange={setAds}
        />
      </ul>
      <div className={styles.dialogMeta}>
        <CustomLink
          className={styles.policyLink}
          href={COOKIE_POLICY_URL}
          target="_blank"
          rel="noreferrer"
        >
          {t('cookieBanner.policyLink')}
        </CustomLink>
      </div>
      <div className={styles.dialogActions}>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.primary}`}
          data-consent-action="accept"
          onClick={onAccept}
        >
          {t('cookieBanner.ACCEPT')}
        </button>
        <button
          type="button"
          className={`${styles.actionButton} ${styles.outline}`}
          data-decline="equal"
          onClick={onDecline}
        >
          {t('cookieBanner.REJECT')}
        </button>
      </div>
      <div className={styles.dialogActions}>
        <button
          type="button"
          className={styles.actionButton}
          data-consent-action="save"
          onClick={() =>
            onSave({
              stats,
              analytics,
              ads: gpc ? false : ads,
            })
          }
        >
          {t('cookieBanner.save')}
        </button>
        <button type="button" className={styles.textButton} onClick={onClose}>
          {t('cookieBanner.cancel')}
        </button>
      </div>
    </dialog>
  );
}

function SwitchRow({
  label,
  description,
  note,
  checked,
  disabled,
  locked,
  onChange,
}: {
  label: string;
  description: string;
  /** Shown under the description and read with the switch. */
  note?: string;
  checked: boolean;
  disabled?: boolean;
  /** Always on. Not part of the saved choice. */
  locked?: boolean;
  onChange?: (next: boolean) => void;
}) {
  const inputId = useId();
  const descriptionId = useId();
  const noteId = useId();
  return (
    <li className={styles.switchRow}>
      <div className={styles.switchCopy}>
        <label htmlFor={inputId} className={styles.switchLabel}>
          {label}
        </label>
        <p id={descriptionId} className={styles.switchDescription}>
          {description}
        </p>
        {note ? (
          <p id={noteId} className={styles.switchNote}>
            {note}
          </p>
        ) : null}
      </div>
      <span className={styles.switchControl}>
        <input
          id={inputId}
          className={
            locked
              ? `${styles.switchInput} ${styles.switchInputLocked}`
              : styles.switchInput
          }
          type="checkbox"
          role="switch"
          checked={checked}
          disabled={disabled}
          aria-describedby={note ? `${descriptionId} ${noteId}` : descriptionId}
          onChange={(event) => onChange?.(event.target.checked)}
        />
        {locked ? (
          <span className={styles.switchLock} aria-hidden="true">
            <Lock />
          </span>
        ) : null}
      </span>
    </li>
  );
}
